import {getTestProps} from '@benchling/js-test-utils';
import {Loader, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, KeyboardEvent, ChangeEvent, useRef, useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatDAO from 'llm/chat-library/ChatDAO';
import {handleSelectFiles as handleSelectFilesHelper} from 'llm/chat-library/ChatFileHelper';
import {ChatFileUpload, ChatSelectedFile} from 'llm/chat-library/ChatLibraryTypes';
import {
  AgentMode,
  getAgentModeIcon,
  getAgentModeText,
  isAgentModeEnabled,
  getAgentModePlaceholderText,
  doesAgentModeSupportPublicData,
  getChatTypes,
  getAvailableAgents,
  mapAgentModeToAgentType,
  ChatTypeByAgentMode,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import useRealtimeVoiceSession, {
  RealtimeVoiceSessionControls,
  RealtimeVoiceSessionState,
} from 'llm/realtime-voice/useRealtimeVoiceSession';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY, GRAY_DARK, GRAY_DARKER, GRAY_LIGHTER, WHITE} from 'ui-library/Colors';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import DropTarget from 'util/drag-and-drop/DropTarget';
import isLikelyMobileCapabilities from 'util/isLikelyMobileCapabilities';

import {trackMessageSent} from './ChatAnalytics';
import {setInitialChatData} from './InitialChatData';
import {NewChatRequest} from './sidepanel/ChatRouter';

export interface ChatInputProps {
  chatSessionId: string | null;
  inputText: string;
  setInputText: (message: string) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  onSubmit: () => void;
  inputDisabled: boolean;
  selectedFiles: Array<ChatSelectedFile>;
  setSelectedFiles: (
    files: Array<ChatSelectedFile> | ((prev: Array<ChatSelectedFile>) => Array<ChatSelectedFile>)
  ) => void;
  agentMode: AgentMode | null;
  setAgentMode: (agentMode: AgentMode | null) => void;
  allowedChatTypes: Array<ChatType>;
  hideAIWarning?: boolean;
}

/**
 * TODO(BNCH-132631) Currently only supports new chats.
 */
export function NewChatInput({
  onCreateChat,
  newChatRequest,
}: {
  onCreateChat: (chat: {id: string; type: ChatType}) => void;
  newChatRequest: NewChatRequest | null;
}): JSX.Element {
  const controls = useChatInputControls(null, newChatRequest?.agentMode ?? null);
  const {inputText, agentMode, publicDataEnabled, getFileUploads} = controls;

  const {isSubmitting, sendMessage} = useNewChat({onCreateChat});

  const handleSubmitMessage = useCallback(async () => {
    if (!inputText.trim() || isSubmitting || !agentMode) {
      return;
    }
    await sendMessage(agentMode, {text: inputText, fileUploads: getFileUploads()}, {publicDataEnabled});
  }, [agentMode, inputText, isSubmitting, publicDataEnabled, sendMessage, getFileUploads]);

  return (
    <ChatInput
      chatSessionId={null}
      {...controls}
      onSubmit={handleSubmitMessage}
      inputDisabled={isSubmitting}
      allowedChatTypes={getChatTypes()}
      hideAIWarning={true}
    />
  );
}

export interface ChatInputControls {
  inputText: string;
  setInputText: (message: string) => void;
  selectedFiles: Array<ChatSelectedFile>;
  setSelectedFiles: (
    files: Array<ChatSelectedFile> | ((prev: Array<ChatSelectedFile>) => Array<ChatSelectedFile>)
  ) => void;
  /**
   * This should only be called during submission.
   * @throws if any files are still uploading.
   */
  getFileUploads: () => Array<ChatFileUpload>;
  agentMode: AgentMode | null;
  setAgentMode: (agentMode: AgentMode | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  allowedChatTypes: Array<ChatType>;
}

// This should be generalized to replace `useDeepResearchMessageThread` and `useDataCaptureMessageThread`.
// It should store data in Redux so that we can return to our pending chat, notably preserving
// optimistic updates. We will want to use an implementation closer to `useDataCaptureMessageThread`.
// Alternatively, all writes should be done through web, instead of being enqueued through celery.
function useNewChat({onCreateChat}: {onCreateChat: (chat: {id: string; type: ChatType}) => void}): {
  isSubmitting: boolean;
  sendMessage: (
    agentMode: AgentMode,
    contents: {
      text: string;
      fileUploads: Array<ChatFileUpload>;
    },
    options: {publicDataEnabled: boolean}
  ) => Promise<void>;
} {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const sendMessage = useCallback(
    async (
      agentMode: AgentMode,
      contents: {
        text: string;
        fileUploads: Array<ChatFileUpload>;
      },
      options: {publicDataEnabled: boolean}
    ) => {
      trackMessageSent({
        chatType: ChatTypeByAgentMode[agentMode],
        agentType: mapAgentModeToAgentType(agentMode),
        messageCount: 0,
        fileCount: contents.fileUploads.length,
        includePublicData: doesAgentModeSupportPublicData(agentMode) ? options.publicDataEnabled : null,
      });
      setIsSubmitting(true);
      try {
        const rv = await ChatDAO.sendMessage(null, agentMode, contents, options);
        setInitialChatData(rv.chatId, {
          statusUrl: rv.statusUrl,
          message: {...contents, agentType: mapAgentModeToAgentType(agentMode)},
        });
        onCreateChat({id: rv.chatId, type: rv.type});
      } finally {
        setIsSubmitting(false);
      }
    },
    [onCreateChat]
  );
  return {isSubmitting, sendMessage};
}

export const ChatInputGlobalClientState = makeUseGlobalClientState<{
  lastUsedAgent: AgentMode | null;
  lastUsedPublicDataEnabled: boolean;
}>({lastUsedAgent: null, lastUsedPublicDataEnabled: false});

/**
 * State for agent settings
 * ========================
 *
 * ## Agent Mode
 * For new chats, pick the first available agent (typically "Ask"), unless initialAgentMode is provided.
 * For existing chats, use the last used agent.
 *
 * This has the behavior that a new chat starts fresh from Ask, while typically an existing chat will
 * start off with the agent that was just used. If the user decides to toggle to a different agent,
 * and comes back to a chat, we will keep the input in the same state. We should revisit this once
 * we decide to have per-chat draft state, which would include both the message text and the agent mode.
 *
 * ## Public Data
 * Public data state is simply global for the current browser session. Whenever Deep Research is selected,
 * the last used value for "Include public data" will be used used. Note that `publicDataEnabled` will
 * remain `true` even if the chosen agentMode does not support public data. Downstream code is responsible
 * for hiding the toggle / ignoring the value if the agentMode does not support public data.
 */
function useAgentSettings(
  initialState:
    | {existingChatType: ChatType | null; initialAgentMode?: never}
    | {existingChatType?: never; initialAgentMode: AgentMode}
): {
  agentMode: AgentMode | null;
  setAgentMode: (agentMode: AgentMode) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
} {
  // For new chats, we pick the first available agent (typically "Ask").
  // For existing chats, use the last used agent.

  const [state, setState] = ChatInputGlobalClientState.useGlobalClientState('');
  const availableAgents = useMemo(
    () =>
      getAvailableAgents(initialState.existingChatType ? [initialState.existingChatType] : getChatTypes()),
    [initialState.existingChatType]
  );

  const [newChatAgentMode, _setNewChatAgentMode] = useState<AgentMode | null>(
    initialState.initialAgentMode && availableAgents.includes(initialState.initialAgentMode)
      ? initialState.initialAgentMode
      : (availableAgents[0] ?? null)
  );

  const lastUsedAgentMode = useMemo(
    () =>
      state.lastUsedAgent && availableAgents.includes(state.lastUsedAgent)
        ? state.lastUsedAgent
        : availableAgents[0],
    [state.lastUsedAgent, availableAgents]
  );

  const setAgentMode = useCallback(
    (agentMode: AgentMode) => {
      if (!initialState.existingChatType) {
        _setNewChatAgentMode(agentMode);
      }
      setState({...state, lastUsedAgent: agentMode});
    },
    [_setNewChatAgentMode, state, setState, initialState.existingChatType]
  );

  const publicDataEnabled = state.lastUsedPublicDataEnabled;

  const setPublicDataEnabled = useCallback(
    (publicDataEnabled: boolean) => {
      setState({...state, lastUsedPublicDataEnabled: publicDataEnabled});
    },
    [state, setState]
  );

  const agentMode = initialState.existingChatType ? lastUsedAgentMode : newChatAgentMode;
  return {agentMode, setAgentMode, publicDataEnabled, setPublicDataEnabled};
}

/**
 * Custom hook for managing related input state.
 */
export function useChatInputControls(
  existingChatType: ChatType | null,
  initialAgentMode: AgentMode | null = null
): ChatInputControls {
  const [inputText, setInputText] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<Array<ChatSelectedFile>>([]);
  const {agentMode, setAgentMode, publicDataEnabled, setPublicDataEnabled} = useAgentSettings(
    initialAgentMode ? {initialAgentMode} : {existingChatType}
  );
  const getFileUploads = useCallback(
    () =>
      selectedFiles.map((file) => {
        assert(file.completedUpload != null, 'Unexpected submit with files still uploading.');
        return file.completedUpload;
      }),
    [selectedFiles]
  );

  const allowedChatTypes = useMemo(
    () => (existingChatType ? [existingChatType] : getChatTypes()),
    [existingChatType]
  );

  return {
    inputText,
    setInputText,
    agentMode,
    setAgentMode,
    publicDataEnabled,
    setPublicDataEnabled,
    selectedFiles,
    setSelectedFiles,
    getFileUploads,
    allowedChatTypes,
  };
}

export function ChatInput({
  chatSessionId,
  inputText,
  setInputText,
  publicDataEnabled,
  setPublicDataEnabled,
  onSubmit,
  inputDisabled,
  selectedFiles,
  setSelectedFiles,
  agentMode,
  setAgentMode,
  allowedChatTypes,
  hideAIWarning,
}: ChatInputProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const realtimeVoiceSessionControls = useRealtimeVoiceSession(chatSessionId);

  const hasUploadingFiles = selectedFiles.some((file) => file.completedUpload == null);
  const isSubmitDisabled =
    inputDisabled ||
    (!inputText.trim() && selectedFiles.length === 0) ||
    hasUploadingFiles ||
    agentMode == null;

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setInputText(e.target.value);
    autoResize();
  };

  const autoResize = (): void => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // Enter (without shift) should submit the message if possible, and never type a newline.
        e.preventDefault();
        if (!isSubmitDisabled) {
          onSubmit();
        }
      }
    },
    [isSubmitDisabled, onSubmit]
  );

  const handleRemoveFile = useCallback(
    (file: File): void => {
      if (!inputDisabled) {
        setSelectedFiles((selectedFiles) => selectedFiles.filter((f) => f.file !== file));
      }
    },
    [inputDisabled, setSelectedFiles]
  );

  const handleSelectFiles = useCallback(
    (files: Array<File>): void => {
      handleSelectFilesHelper(files, setSelectedFiles);
    },
    [setSelectedFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    },
    [handleSelectFiles]
  );

  const handleDrop = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      if (inputDisabled) {
        return;
      }

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
    },
    [inputDisabled, handleSelectFiles]
  );

  return (
    <div
      ref={inputContainerRef}
      className={css(styles.inputContainer)}
      {...getTestProps({component: 'ChatInput', element: 'root'})}
    >
      <DropTarget
        shouldCaptureDrop={true}
        mimeTypes={['Files']}
        dropEffect="copy"
        targetDesc="chat input"
        onDrop={handleDrop}
      >
        <div
          className={css(styles.inputGroup, inputDisabled && styles.inputGroupDisabled)}
          {...getTestProps({component: 'ChatInput', element: 'fileDropZone'})}
        >
          <textarea
            ref={textareaRef}
            className={css(styles.input)}
            placeholder={
              // No default agent is unexpected, but it's a valid state, so we handle it gracefully.
              agentMode != null ? getAgentModePlaceholderText(agentMode) : 'Pick an agent to begin.'
            }
            autoFocus={true}
            value={inputText}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            disabled={inputDisabled}
          />
          {selectedFiles.length > 0 && (
            <div className={css(styles.fileListWrapper)}>
              <FileList>
                {selectedFiles.map((selectedFile) => (
                  <FileRow
                    key={selectedFile.key}
                    file={selectedFile.file}
                    onRequestRemove={handleRemoveFile}
                    additionalContent={!selectedFile.completedUpload && <Loader size="sm" />}
                  />
                ))}
              </FileList>
            </div>
          )}
          <div className={css(styles.inputGroupButtonRow)}>
            <div className={css(styles.inputGroupIconButtons)}>
              {/* Invisible input to trigger file picker */}
              <input
                {...getTestProps({component: 'ChatInput', element: 'fileInput'})}
                type="file"
                multiple={true}
                className="hidden"
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
              {/* Invisible input to trigger camera */}
              <input
                {...getTestProps({component: 'ChatInput', element: 'cameraInput'})}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                ref={cameraInputRef}
                onChange={handleFileInputChange}
              />
              <AgentToolbar
                chatSessionId={chatSessionId}
                textareaRef={textareaRef}
                fileInputRef={fileInputRef}
                cameraInputRef={cameraInputRef}
                allowedChatTypes={allowedChatTypes}
                agentMode={agentMode}
                setAgentMode={setAgentMode}
                inputDisabled={inputDisabled}
                publicDataEnabled={publicDataEnabled}
                setPublicDataEnabled={setPublicDataEnabled}
                realtimeVoiceSessionControls={realtimeVoiceSessionControls}
              />
            </div>
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.MINI}
              onClick={onSubmit}
              disabled={isSubmitDisabled}
              icon="arrow-up"
              iconAltText="Send"
            />
          </div>
        </div>
      </DropTarget>
      {!hideAIWarning && (
        <div className={css(styles.warningMessage)}>AI can make mistakes. Always check results.</div>
      )}
    </div>
  );
}

function AgentToolbar({
  chatSessionId,
  textareaRef,
  fileInputRef,
  cameraInputRef,
  allowedChatTypes,
  agentMode,
  setAgentMode,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  realtimeVoiceSessionControls,
}: {
  chatSessionId: string | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  allowedChatTypes: Array<ChatType>;
  agentMode: AgentMode | null;
  setAgentMode: (agentMode: AgentMode | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  cameraInputRef: React.RefObject<HTMLInputElement>;
  inputDisabled: boolean;
  realtimeVoiceSessionControls: RealtimeVoiceSessionControls;
}): JSX.Element {
  const hasAskAgentMode = isAgentModeEnabled(AgentMode.ASK);
  const isOpenAIEnabled = BenchlingConfig.LLM_ENABLE_OPENAI;

  const handleAttachButtonClick = useCallback((): void => {
    if (!inputDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [inputDisabled, fileInputRef]);

  const handleCameraButtonClick = useCallback((): void => {
    if (!inputDisabled && cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, [inputDisabled, cameraInputRef]);

  return (
    <div
      className={css(styles.agentModeButtons)}
      onMouseDown={(e) => {
        const textArea = textareaRef.current;
        if (!textArea) {
          return;
        }
        if (document.activeElement === textArea) {
          // Don't steal focus from the text area
          e.preventDefault();
        } else {
          // Focus is if it's not already.
          textArea.focus();
        }
      }}
    >
      {getAvailableAgents(allowedChatTypes).map((mode: AgentMode) => {
        const isSpecializedAgent = agentMode != null && agentMode !== AgentMode.ASK;

        // We will only render the selected agent if the selected agent is notASK.
        if (isSpecializedAgent && mode !== agentMode) {
          return null;
        }
        // It we are just showing the selected agent, then provide a close icon to see all agents...
        // ... but only if there is another agent to show.
        const showCloseIcon = isSpecializedAgent && getAvailableAgents(allowedChatTypes).length > 1;
        const toggleButton = (
          <ToggleButton
            key={mode}
            isActive={mode === agentMode}
            showCloseIcon={showCloseIcon}
            onClick={() => {
              setAgentMode(isSpecializedAgent ? (hasAskAgentMode ? AgentMode.ASK : null) : mode);
            }}
            icon={getAgentModeIcon(mode) ?? undefined}
            text={getAgentModeText(mode)}
            disabled={inputDisabled}
          />
        );

        if (mode === AgentMode.ASK) {
          return (
            <Tooltip key={mode} label="Find answers fast" position="bottom">
              <span style={{display: 'inline-block'}}>{toggleButton}</span>
            </Tooltip>
          );
        }

        if (mode === AgentMode.DEEP_RESEARCH) {
          return (
            <Tooltip key={mode} label="Answer complex questions" position="bottom">
              <span style={{display: 'inline-block'}}>{toggleButton}</span>
            </Tooltip>
          );
        }

        if (mode === AgentMode.DATA_CAPTURE) {
          return (
            <Tooltip key={mode} label="Create lab entries" position="bottom">
              <span style={{display: 'inline-block'}}>{toggleButton}</span>
            </Tooltip>
          );
        }

        return toggleButton;
      })}
      {agentMode != null && doesAgentModeSupportPublicData(agentMode) && (
        <Tooltip
          label={
            isOpenAIEnabled
              ? 'Use public data to help answer your question.'
              : 'OpenAI must be enabled to use this feature.'
          }
          position="bottom"
        >
          <span style={{display: 'inline-block'}}>
            <ToggleButton
              isActive={publicDataEnabled}
              onClick={() => {
                setPublicDataEnabled(!publicDataEnabled);
              }}
              showCloseIcon={false}
              icon="globe"
              text="Include public data"
              disabled={inputDisabled || !isOpenAIEnabled}
            />
          </span>
        </Tooltip>
      )}
      <Tooltip label="Add files" position="bottom">
        <span style={{display: 'inline-block'}}>
          <Button
            aria-label="Upload attachments"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={inputDisabled}
            icon="paperclip"
            onClick={handleAttachButtonClick}
            size={ButtonSize.MINI}
          />
        </span>
      </Tooltip>
      {isLikelyMobileCapabilities() && (
        <Button
          aria-label="Take and upload photo"
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          disabled={inputDisabled}
          icon="snapshot"
          onClick={handleCameraButtonClick}
          size={ButtonSize.MINI}
        />
      )}
      {BenchlingConfig.LLM_ENABLE_REALTIME_VOICE_IN_COMPOSE &&
        agentMode === AgentMode.DATA_CAPTURE &&
        chatSessionId != null &&
        (() => {
          switch (realtimeVoiceSessionControls.sessionState) {
            case RealtimeVoiceSessionState.NOT_CONNECTED:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={realtimeVoiceSessionControls.startVoiceSession}
                  disabled={inputDisabled}
                  icon="sparkles"
                  text="Go live"
                />
              );
            case RealtimeVoiceSessionState.CONNECTING:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  disabled={true}
                  text="Connecting..."
                />
              );
            case RealtimeVoiceSessionState.CONNECTED:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={realtimeVoiceSessionControls.stopVoiceSession}
                  disabled={inputDisabled}
                  icon="stop"
                  text="Stop"
                />
              );
            default:
              return null;
          }
        })()}
    </div>
  );
}

/**
 * A toggle button used to pick an agent or an agent feature like public data.
 */
function ToggleButton(props: {
  isActive: boolean;
  text: React.ReactNode;
  showCloseIcon: boolean;
  onClick: () => void;
  icon: BenchlingIconName | undefined;
  disabled: boolean;
}): JSX.Element {
  const {isActive, text, showCloseIcon, onClick, icon, disabled} = props;
  return (
    // NB: I did not see an out of the box toggle button in Mantine v6.
    <Button
      aria-pressed={isActive}
      className={classNames({'is-active': isActive}, css(styles.agentModeButton))}
      size={ButtonSize.MINI}
      buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
      onClick={onClick}
      icon={icon}
      textClassName={css(styles.agentModeText)}
      text={
        <>
          {text}
          {showCloseIcon && <BenchlingIcon className={css(styles.closeIcon)} icon="close" />}
        </>
      }
      disabled={disabled}
    />
  );
}

const styles = StyleSheet.create({
  inputContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '4px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `1px solid ${GRAY}`,
    borderRadius: '16px',
    backgroundColor: WHITE,
    transition: 'box-shadow 0.1s ease-in',
    ':focus-within': {
      border: `1px solid ${GRAY_DARK}`,
      boxShadow: '0px 0px 2px 0px #00000040',
    },
  },
  inputGroupDisabled: {
    backgroundColor: GRAY_LIGHTER,
  },
  input: {
    flexGrow: 1,
    width: '100%',
    minHeight: '80px',
    padding: '12px',
    fontFamily: 'inherit',
    fontSize: '16px',
    border: 'none',
    background: 'transparent',
    resize: 'none',
    overflow: 'hidden',
    ':focus': {
      outline: 'none',
    },
  },
  inputGroupButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
  },
  inputGroupIconButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  publicDataToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  emailToggle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4px 0',
  },
  warningMessage: {
    fontSize: '12px',
    color: GRAY_DARKER,
    textAlign: 'center',
    marginTop: '4px',
    marginBottom: '8px',
  },
  fileListWrapper: {
    padding: '8px 12px',
    borderTop: `1px solid ${GRAY_LIGHTER}`,
    // Ugly hack: The global `table` class on FileList adds 17px margin-bottom. Offset that with a
    // negative margin so that this component isn't imposing its own margins and instead lets its
    // parent handle spacing.
    marginBottom: -17,
  },
  indicator: {
    backgroundColor: BLUE_LIGHT,
  },
  agentModeButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  agentModeButton: {
    fontWeight: 600,
    fontSize: '12px',
    padding: '4px 6px', // reduce horizontal padding, we are in tight space.
  },
  closeIcon: {
    marginLeft: '6px',
    marginRight: 0, // button has padding.
  },
  agentModeText: {
    flex: 'none',
  },
});
